import { WatchlistItem } from './store';
import { getUserInteractions } from './user-analytics';

interface LanguageProfile {
  primary: string[];
  secondary: string[];
  excluded: string[];
  autoDetected: boolean;
}

interface LanguageScore {
  language: string;
  score: number;
  count: number;
  lastUsed?: Date;
}

export class LanguagePreferenceDetector {
  private static readonly COMMON_LANGUAGES = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    ru: 'Russian',
    ja: 'Japanese',
    ko: 'Korean',
    zh: 'Chinese',
    hi: 'Hindi',
    ar: 'Arabic',
    nl: 'Dutch',
    sv: 'Swedish',
    no: 'Norwegian',
    da: 'Danish',
    fi: 'Finnish',
    pl: 'Polish',
    tr: 'Turkish',
    he: 'Hebrew',
  };

  private static readonly LANGUAGE_FAMILIES = {
    romance: ['es', 'fr', 'it', 'pt', 'ro'],
    germanic: ['de', 'nl', 'sv', 'no', 'da', 'en'],
    slavic: ['ru', 'pl', 'cs', 'sk', 'uk'],
    asian: ['ja', 'ko', 'zh', 'th', 'vi'],
    indic: ['hi', 'ta', 'te', 'bn', 'mr'],
    semitic: ['ar', 'he'],
  };

  static async detectUserLanguages(
    userId: string,
    watchlist: WatchlistItem[],
    interactions?: any[]
  ): Promise<LanguageProfile> {
    const languageScores = new Map<string, LanguageScore>();

    // Analyze watchlist
    watchlist.forEach(item => {
      // Weight by personal ranking and recency
      const weight = this.calculateWeight(item);
      this.updateLanguageScore(languageScores, item, weight);
    });

    // Analyze interactions if provided
    if (interactions) {
      interactions.forEach(interaction => {
        if (interaction.language) {
          const score = languageScores.get(interaction.language) || {
            language: interaction.language,
            score: 0,
            count: 0,
          };
          score.score += 1;
          score.count += 1;
          score.lastUsed = interaction.timestamp;
          languageScores.set(interaction.language, score);
        }
      });
    }

    // Get browser language as fallback
    const browserLang = this.getBrowserLanguage();
    if (!languageScores.has(browserLang)) {
      languageScores.set(browserLang, {
        language: browserLang,
        score: 0.5, // Give it some weight but not primary
        count: 1,
      });
    }

    // Convert to sorted array
    const sortedLanguages = Array.from(languageScores.values())
      .sort((a, b) => b.score - a.score);

    // Determine primary and secondary languages
    const profile = this.categorizeLanguages(sortedLanguages);

    return profile;
  }

  private static calculateWeight(item: WatchlistItem): number {
    let weight = 1.0;

    // Personal ranking weight
    if (item.personalRank) {
      weight *= (item.personalRank / 10) * 1.5;
    }

    // Recency weight
    const daysAgo = (Date.now() - new Date(item.addedAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysAgo < 7) weight *= 1.5;
    else if (daysAgo < 30) weight *= 1.2;
    else if (daysAgo < 90) weight *= 1.0;
    else weight *= 0.8;

    return weight;
  }

  private static updateLanguageScore(
    scores: Map<string, LanguageScore>,
    item: any,
    weight: number
  ): void {
    // This would need actual language data from TMDB
    // For now, we'll simulate with the overview language detection
    const detectedLang = this.detectLanguageFromText(item.overview || '');

    if (detectedLang) {
      const score = scores.get(detectedLang) || {
        language: detectedLang,
        score: 0,
        count: 0,
      };
      score.score += weight;
      score.count += 1;
      scores.set(detectedLang, score);
    }
  }

  private static detectLanguageFromText(text: string): string | null {
    // Simple heuristic-based language detection
    // In production, you'd use a proper language detection library

    if (!text || text.length < 20) return null;

    const patterns: { [key: string]: RegExp[] } = {
      en: [/\b(the|and|of|to|in|is|was|that|for)\b/gi],
      es: [/\b(el|la|de|que|en|y|los|las|un|una)\b/gi],
      fr: [/\b(le|la|de|et|à|les|un|une|dans|pour)\b/gi],
      de: [/\b(der|die|das|und|in|von|zu|mit|den|ein)\b/gi],
      it: [/\b(il|la|di|e|che|in|un|per|con|una)\b/gi],
      pt: [/\b(o|a|de|e|que|em|os|as|um|uma)\b/gi],
      ja: [/[\u3040-\u309f\u30a0-\u30ff]/],
      ko: [/[\uac00-\ud7af]/],
      zh: [/[\u4e00-\u9fff]/],
    };

    const scores: { [key: string]: number } = {};

    for (const [lang, regexes] of Object.entries(patterns)) {
      let matches = 0;
      regexes.forEach(regex => {
        const found = text.match(regex);
        if (found) matches += found.length;
      });
      if (matches > 0) {
        scores[lang] = matches;
      }
    }

    // Return language with highest score
    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 ? sorted[0][0] : 'en'; // Default to English
  }

  private static categorizeLanguages(
    sortedLanguages: LanguageScore[]
  ): LanguageProfile {
    const primary: string[] = [];
    const secondary: string[] = [];
    const excluded: string[] = [];

    if (sortedLanguages.length === 0) {
      return {
        primary: ['en'], // Default to English
        secondary: [],
        excluded: [],
        autoDetected: true,
      };
    }

    const totalScore = sortedLanguages.reduce((sum, lang) => sum + lang.score, 0);

    sortedLanguages.forEach((lang, index) => {
      const percentage = (lang.score / totalScore) * 100;

      if (index === 0 || percentage > 20) {
        // Primary language: first language or >20% of content
        primary.push(lang.language);
      } else if (percentage > 5) {
        // Secondary language: 5-20% of content
        secondary.push(lang.language);
      } else if (lang.count === 0 || percentage < 1) {
        // Excluded: never watched or <1% of content
        excluded.push(lang.language);
      }
    });

    // Add related languages to secondary
    primary.forEach(primaryLang => {
      const related = this.getRelatedLanguages(primaryLang);
      related.forEach(relLang => {
        if (!primary.includes(relLang) && !secondary.includes(relLang)) {
          secondary.push(relLang);
        }
      });
    });

    return {
      primary,
      secondary: secondary.slice(0, 3), // Limit secondary to 3
      excluded,
      autoDetected: true,
    };
  }

  private static getRelatedLanguages(language: string): string[] {
    const related: string[] = [];

    for (const [family, languages] of Object.entries(this.LANGUAGE_FAMILIES)) {
      if (languages.includes(language)) {
        // Add other languages from the same family
        languages.forEach(lang => {
          if (lang !== language) {
            related.push(lang);
          }
        });
        break;
      }
    }

    return related;
  }

  private static getBrowserLanguage(): string {
    const browserLang = navigator.language || 'en';
    return browserLang.split('-')[0]; // Get just the language code
  }

  static filterByLanguage(
    items: any[],
    profile: LanguageProfile,
    strict: boolean = true
  ): any[] {
    return items.filter(item => {
      const itemLang = item.original_language;

      // Always include if in primary languages
      if (profile.primary.includes(itemLang)) {
        return true;
      }

      // Include secondary languages if not strict
      if (!strict && profile.secondary.includes(itemLang)) {
        return true;
      }

      // Exclude if in excluded list
      if (profile.excluded.includes(itemLang)) {
        return false;
      }

      // Check if content has audio/subtitles in user's languages
      if (item.spoken_languages) {
        const hasUserLanguage = item.spoken_languages.some((lang: any) =>
          profile.primary.includes(lang.iso_639_1) ||
          (!strict && profile.secondary.includes(lang.iso_639_1))
        );
        if (hasUserLanguage) return true;
      }

      // If strict mode, exclude unknown languages
      // If not strict, include them with lower priority
      return !strict;
    });
  }

  static scoreLanguageMatch(
    item: any,
    profile: LanguageProfile
  ): number {
    const itemLang = item.original_language;

    // Primary language: full score
    if (profile.primary.includes(itemLang)) {
      return 1.0;
    }

    // Secondary language: partial score
    if (profile.secondary.includes(itemLang)) {
      return 0.6;
    }

    // Excluded language: very low score
    if (profile.excluded.includes(itemLang)) {
      return 0.1;
    }

    // Check for audio/subtitle availability
    if (item.spoken_languages) {
      const hasPrimary = item.spoken_languages.some((lang: any) =>
        profile.primary.includes(lang.iso_639_1)
      );
      if (hasPrimary) return 0.7;

      const hasSecondary = item.spoken_languages.some((lang: any) =>
        profile.secondary.includes(lang.iso_639_1)
      );
      if (hasSecondary) return 0.4;
    }

    // Unknown language: low score
    return 0.2;
  }

  static getLanguageBadge(
    language: string,
    profile: LanguageProfile
  ): { text: string; color: string } | null {
    if (!this.COMMON_LANGUAGES[language as keyof typeof this.COMMON_LANGUAGES]) {
      return null;
    }

    const langName = this.COMMON_LANGUAGES[language as keyof typeof this.COMMON_LANGUAGES];

    if (profile.primary.includes(language)) {
      return null; // Don't show badge for primary language
    }

    if (profile.secondary.includes(language)) {
      return {
        text: langName,
        color: 'blue', // Secondary language
      };
    }

    if (profile.excluded.includes(language)) {
      return {
        text: langName,
        color: 'red', // Excluded language
      };
    }

    return {
      text: langName,
      color: 'gray', // Unknown/neutral language
    };
  }

  static async saveUserLanguagePreference(
    userId: string,
    profile: LanguageProfile
  ): Promise<void> {
    // Save to localStorage for quick access
    localStorage.setItem(
      `language_profile_${userId}`,
      JSON.stringify(profile)
    );

    // Would also save to Firebase here
    // await saveToFirebase(userId, profile);
  }

  static async loadUserLanguagePreference(
    userId: string
  ): Promise<LanguageProfile | null> {
    // Try localStorage first
    const cached = localStorage.getItem(`language_profile_${userId}`);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
      }
    }

    // Would also try Firebase here
    // return await loadFromFirebase(userId);

    return null;
  }
}

// Helper functions for easy access
export async function detectUserLanguages(
  userId: string,
  watchlist: WatchlistItem[]
): Promise<LanguageProfile> {
  // First try to load saved preferences
  const saved = await LanguagePreferenceDetector.loadUserLanguagePreference(userId);
  if (saved && !saved.autoDetected) {
    return saved; // User has manually set preferences
  }

  // Otherwise detect from watch history
  const interactions = await getUserInteractions(userId);
  const detected = await LanguagePreferenceDetector.detectUserLanguages(
    userId,
    watchlist,
    interactions
  );

  // Save the detected preferences
  await LanguagePreferenceDetector.saveUserLanguagePreference(userId, detected);

  return detected;
}

export function filterByLanguage(
  items: any[],
  profile: LanguageProfile,
  strict: boolean = true
): any[] {
  return LanguagePreferenceDetector.filterByLanguage(items, profile, strict);
}

export function scoreLanguageMatch(
  item: any,
  profile: LanguageProfile
): number {
  return LanguagePreferenceDetector.scoreLanguageMatch(item, profile);
}

export function getLanguageBadge(
  language: string,
  profile: LanguageProfile
): { text: string; color: string } | null {
  return LanguagePreferenceDetector.getLanguageBadge(language, profile);
}